import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  MultiSelect,
} from "@/components/ui";
import type { Matter, MedicalTreatment } from "@/lib/types";
import { formatDate } from "@/lib/utils/format";
import dayjs from "dayjs";
import { useRef, useState } from "react";
import { useFormContext, type UseFormRegisterReturn } from "react-hook-form";

interface SelectTreatmentAppointmentsFormFieldProps {
  className?: string;
  emptyMessage?: string;
  isItemsError?: boolean;
  isItemsLoading?: boolean;
  label?: string;
  matter: Matter;
  treatment?: MedicalTreatment;
  onChange?: (value: string[]) => void;
  placeholder?: string;
  ref?: React.RefObject<HTMLDivElement>;
  register: UseFormRegisterReturn;
  disabled?: boolean;
}

export const SelectTreatmentAppointmentsFormField = ({
  className,
  label,
  matter,
  treatment,
  onChange,
  placeholder,
  register,
  disabled,
  isItemsLoading,
}: SelectTreatmentAppointmentsFormFieldProps) => {
  const form = useFormContext();
  // This is needed to allow the select to be disabled when it is inside a disabled fieldset
  const triggerRef = useRef<HTMLButtonElement>(null);
  const isInDisabledFieldset = Boolean(
    triggerRef.current?.closest("fieldset:disabled"),
  );
  const [selectedValues, setSelectedValues] = useState<string[]>(
    (form.getValues(register.name) as string[] | undefined) ?? [],
  );
  const items = matter.medicalTreatmentAppointments
    .filter((appointment) =>
      treatment ? appointment.medicalTreatmentId === treatment.id : true,
    )
    .sort((a, b) => {
      return dayjs(b.date).diff(dayjs(a.date));
    });

  return (
    <FormField
      control={form.control}
      {...register}
      // @ts-expect-error This line is to remove a console error
      ref={null}
      render={({ field }) => {
        return (
          <FormItem>
            {label && (
              <div className="flex">
                <FormLabel>{label}</FormLabel>
                <FormMessage
                  data-testid="error-message"
                  className="grow text-right"
                />
              </div>
            )}
            <FormControl>
              <MultiSelect
                selectedValues={selectedValues}
                setSelectedValues={setSelectedValues}
                isLoading={isItemsLoading}
                modalPopover
                {...field}
                disabled={isInDisabledFieldset || disabled}
                onValueChange={(value) => {
                  onChange?.(value);
                  field.onChange(value, {
                    shouldDirty: true,
                  });
                }}
                defaultValue={field.value}
                options={items.map((item) => ({
                  label: formatDate(item.date) ?? "<No Date>",
                  value: item.id,
                  context: item.description,
                }))}
                className={className}
                maxCount={1}
                placeholder={placeholder}
                ignoreAllSelected={true}
                showToggleAll={false}
              />
            </FormControl>
          </FormItem>
        );
      }}
    />
  );
};
